import { Link } from "react-router-dom";
import { Flame, Dumbbell, LineChart, BellRing, Sparkles, ArrowRight, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxtdXNjdWxhciUyMGF0aGxldGUlMjB0cmFpbmluZ3xlbnwwfHx8fDE3ODU3NjYxMzh8MA&ixlib=rb-4.1.0&q=85";
const AI_IMG = "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwZnV0dXJpc3RpYyUyMGJsdWV8ZW58MHx8fHwxNzg1NzY2MTM4fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Dumbbell, title: "Log Every Rep", desc: "Track workouts, exercises, sets, reps and weight. Watch your volume climb." },
  { icon: LineChart, title: "Body Weight Trends", desc: "Log your weight and visualize progress with clean, honest charts." },
  { icon: BellRing, title: "Gym Reminders", desc: "Set training reminders so you never skip a session again." },
  { icon: Sparkles, title: "AI Physical Coach", desc: "Premium. A tactical AI coach for training and nutrition, 24/7.", premium: true },
];

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/dashboard" : "/auth";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative z-10">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="text-[#FF3B30]" size={26} strokeWidth={2.5} />
            <span className="font-display font-black text-2xl uppercase tracking-tighter">Reprise<span className="text-[#FF3B30]">Fit</span></span>
          </div>
          <Link to={cta} data-testid="nav-cta" className="bg-[#FF3B30] hover:bg-[#FF453A] text-white font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition-transform duration-150 hover:-translate-y-0.5">
            {user ? "Dashboard" : "Start Training"}
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO} alt="athlete" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-28 md:py-40">
          <div className="max-w-2xl fade-up">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF3B30] mb-4">No excuses. Only progress.</div>
            <h1 className="font-display font-black text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.9] mb-6">
              Track your grind.<br />Own your <span className="text-[#FF3B30]">reprise.</span>
            </h1>
            <p className="text-white/70 text-lg mb-8 max-w-lg">
              RepriseFit logs your workouts and body weight, reminds you to hit the gym, and puts an AI physical coach in your corner.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={cta} data-testid="hero-cta" className="group bg-[#FF3B30] hover:bg-[#FF453A] text-white font-bold uppercase tracking-wide px-7 py-3.5 flex items-center gap-2 transition-transform duration-150 hover:-translate-y-0.5 shadow-[0_0_15px_rgba(255,59,48,0.3)]">
                Start Training <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#features" className="border border-white/20 hover:border-white/50 text-white font-bold uppercase tracking-wide px-7 py-3.5 transition-colors">See Features</a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mb-2">Built for the <span className="text-[#FF3B30]">disciplined</span></h2>
        <p className="text-white/50 mb-12">Everything you need to stay accountable.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className={`p-6 border bg-[#121212] transition-transform duration-150 hover:-translate-y-1 ${f.premium ? "border-[#007AFF]/40 hover:border-[#007AFF]" : "border-white/10 hover:border-white/25"}`}>
                <Icon className={f.premium ? "text-[#007AFF]" : "text-[#FF3B30]"} size={30} />
                <h3 className="font-display font-bold text-xl uppercase tracking-tight mt-4 mb-2">{f.title}</h3>
                <p className="text-white/50 text-sm">{f.desc}</p>
                {f.premium && <span className="inline-block mt-3 text-[10px] bg-[#007AFF]/20 text-[#007AFF] px-2 py-1 font-bold uppercase tracking-widest">Premium</span>}
              </div>
            );
          })}
        </div>
      </section>

      {/* AI Coach banner */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden border border-[#007AFF]/30 bg-[#007AFF]/5">
          <div className="grid md:grid-cols-2 items-center">
            <div className="p-10 md:p-14">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF] mb-4">Premium Feature</div>
              <h2 className="font-display font-black text-4xl uppercase tracking-tighter mb-4">Your AI Physical Coach</h2>
              <p className="text-white/70 mb-6">Reprise builds your plan, answers form questions, and keeps you on track with science-based, tactical advice, anytime.</p>
              <ul className="space-y-3 mb-8">
                {["Personalized training guidance", "Nutrition & recovery tips", "Instant answers, 24/7"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-white/80"><Check size={18} className="text-[#007AFF]" /> {t}</li>
                ))}
              </ul>
              <Link to={cta} data-testid="coach-cta" className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#1a8cff] text-white font-bold uppercase tracking-wide px-7 py-3.5 transition-transform duration-150 hover:-translate-y-0.5">
                Unlock Coach <Sparkles size={18} />
              </Link>
            </div>
            <div className="relative h-64 md:h-full min-h-[320px]">
              <img src={AI_IMG} alt="AI coach" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0A0A0A]/60" />
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-12 text-center text-white/40 text-sm">
        <div className="flex items-center justify-center gap-2 mb-2"><Flame className="text-[#FF3B30]" size={18} /><span className="font-display font-black uppercase tracking-tighter text-white">RepriseFit</span></div>
        reprisefit.net — Train hard. Track everything.
      </footer>
    </div>
  );
}
